/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoTelekomProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoTelekomProps {
    /**
     * A string representing the CSS class to be applied to the LogoTelekomIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#E20074'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LogoTelekomIcon element.
     *
     * @default 29
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoTelekom'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoTelekomIcon element.
     *
     * @default 25
     */
    width?: number | string;
}

/**
 * The `LogoTelekom` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoTelekomProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoTelekomIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoTelekomIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoTelekomIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoTelekomComponent = <LogoTelekom className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoTelekom = ({
    className = '',
    color1 = '#E20074',
    height = 29,
    ref,
    testId = 'LogoTelekom',
    width = 25
}: LogoTelekomProps) => (
    <svg
        ref={ref}
        aria-label="Telekom Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 25 29"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M6.22 19.14H.4v-5.8h5.82zM.4 0v9.86h1.75v-.29c0-4.64 2.61-7.54 7.56-7.54H10v20.88c0 2.9-1.17 4.06-4.07 4.06h-.88V29h15.12v-2.03h-.87c-2.9 0-4.07-1.16-4.07-4.06V2.03h.29c4.94 0 7.56 2.9 7.56 7.54v.29h1.74V0zM19 19.14h5.82v-5.8h-5.81z" fill={color1} />
    </svg>
);

LogoTelekom.displayName = 'LogoTelekom';